// ==========================================================================
// lib/loans.ts — where a loan stands (FF-045, 9 Oct 2026)
//   James: "we need to wash that against the loan which is a redraw (have that
//   as a option in the system when setting it up) mines a offset".
//   Owing comes from the latest statement balance (typed or from a CSV), or
//   the opening balance plus the lines since. Redraw: available = limit − owing.
//   Offset: interest is charged on owing − the offset balances.
// ==========================================================================
import { useAccounts, type Account } from "./money";
import { useRecSummary, type Summary } from "./reconcile";

export const LOAN_STYLES: { key: NonNullable<Account["loan_style"]>; label: string; hint: string }[] = [
  { key: "redraw", label: "Redraw", hint: "Spare money is paid into the loan and can be drawn back out, up to the limit." },
  { key: "offset", label: "Offset", hint: "A separate account sits against the loan; interest is only charged on the difference." },
  { key: "plain", label: "Neither", hint: "A plain loan." },
];

/** An account's balance and the date it's from. Loans come back as a positive amount owing. */
export function balanceOf(a: Account | undefined, summary: Summary[] | undefined): { cents: number; asAt: string | null } | null {
  if (!a) return null;
  const s = summary?.find((x) => x.account_id === a.id);
  let v: number | null = null; let asAt: string | null = null;
  if (s?.statement_balance_cents !== null && s?.statement_balance_cents !== undefined) { v = s.statement_balance_cents; asAt = s.statement_date; }
  else if (a.opening_balance_cents !== null) { v = a.opening_balance_cents + (s?.app_sum_to_statement_cents ?? 0); asAt = s?.last_date ?? a.opening_date; }
  if (v === null) return null;
  return { cents: a.kind === "loan" ? Math.abs(v) : v, asAt };
}

export type LoanPosition = {
  loan: Account; owing: number | null; asAt: string | null;
  limit: number | null; available: number | null;                 // redraw
  offsets: { account: Account; cents: number | null }[]; offsetTotal: number; // offset
  interestBearing: number | null; monthlyInterest: number | null;
};

export function useLoanPosition(loanId: string | null | undefined): LoanPosition | null {
  const accounts = useAccounts(); const summary = useRecSummary();
  const all = accounts.data ?? [];
  const loan = all.find((a) => a.id === loanId);
  if (!loan) return null;
  const b = balanceOf(loan, summary.data);
  const owing = b?.cents ?? null;
  const offsets = all.filter((a) => a.offset_for_account_id === loan.id && !a.closed_on).map((a) => ({ account: a, cents: balanceOf(a, summary.data)?.cents ?? null }));
  const offsetTotal = offsets.reduce((t, o) => t + Math.max(0, o.cents ?? 0), 0);
  const interestBearing = owing === null ? null : Math.max(0, owing - (loan.loan_style === "offset" ? offsetTotal : 0));
  return {
    loan, owing, asAt: b?.asAt ?? null,
    limit: loan.credit_limit_cents, available: loan.loan_style === "redraw" && loan.credit_limit_cents !== null && owing !== null ? Math.max(0, loan.credit_limit_cents - owing) : null,
    offsets, offsetTotal, interestBearing,
    monthlyInterest: interestBearing !== null && loan.interest_rate_pct !== null ? Math.round(interestBearing * loan.interest_rate_pct / 100 / 12) : null,
  };
}
